import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type { BreadcrumbItem } from 'components/Layout/Header/types'

interface LayoutConfig {
  /** Entity breadcrumbs for chain/door detail pages */
  entityBreadcrumbs: BreadcrumbItem[]
  /** Dynamic category info for user-generated/unmatched pages */
  categoryInfo: {
    categorySlug: string
    subCategorySlug: string
  } | null
}

interface LayoutConfigContextValue {
  config: LayoutConfig
  setEntityBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void
  setCategoryInfo: (info: LayoutConfig['categoryInfo']) => void
}

const defaultConfig: LayoutConfig = {
  entityBreadcrumbs: [],
  categoryInfo: null,
}

const LayoutConfigContext = createContext<LayoutConfigContextValue | null>(null)

export function LayoutConfigProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [config, setConfig] = useState<LayoutConfig>(defaultConfig)

  const setEntityBreadcrumbs = useCallback((breadcrumbs: BreadcrumbItem[]) => {
    setConfig((prev) => ({ ...prev, entityBreadcrumbs: breadcrumbs }))
  }, [])

  const setCategoryInfo = useCallback((info: LayoutConfig['categoryInfo']) => {
    setConfig((prev) => ({ ...prev, categoryInfo: info }))
  }, [])

  const value = useMemo(
    () => ({ config, setEntityBreadcrumbs, setCategoryInfo }),
    [config, setEntityBreadcrumbs, setCategoryInfo]
  )

  return (
    <LayoutConfigContext.Provider value={value}>
      {children}
    </LayoutConfigContext.Provider>
  )
}

export function useLayoutConfig(): LayoutConfigContextValue {
  const context = useContext(LayoutConfigContext)
  if (!context) {
    // Return a no-op version if used outside provider (for backwards compatibility)
    return {
      config: defaultConfig,
      setEntityBreadcrumbs: () => {},
      setCategoryInfo: () => {},
    }
  }
  return context
}

/**
 * Hook to set entity breadcrumbs (Chain/Door names) on mount and clear on unmount.
 * Use this in entity detail pages (chain pages, door pages) to show the entity name in breadcrumbs.
 *
 * @param breadcrumbs - Array of breadcrumb items to display (e.g., [{ label: 'Chain: McDonald\'s' }])
 *
 * Note: Callers should memoize the breadcrumbs array to prevent unnecessary re-renders.
 * Use useMemo() to create a stable reference when passing breadcrumbs to this hook.
 */
export function useEntityBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
  const { setEntityBreadcrumbs } = useLayoutConfig()

  React.useEffect(() => {
    setEntityBreadcrumbs(breadcrumbs)
    return () => setEntityBreadcrumbs([])
  }, [setEntityBreadcrumbs, breadcrumbs])
}

/**
 * Hook to set dynamic category info for user-generated/unmatched pages.
 * Use this in pages where the category cannot be determined from the URL path.
 *
 * @param categorySlug - The category slug (e.g., 'commercial', 'non-commercial', 'retail')
 * @param subCategorySlug - The subcategory slug (e.g., 'restaurants-bars', 'education-cu')
 */
export function useDynamicCategoryInfo(
  categorySlug: string | undefined,
  subCategorySlug: string | undefined
): void {
  const { setCategoryInfo } = useLayoutConfig()

  React.useEffect(() => {
    if (categorySlug && subCategorySlug) {
      setCategoryInfo({ categorySlug, subCategorySlug })
    }
    return () => setCategoryInfo(null)
  }, [setCategoryInfo, categorySlug, subCategorySlug])
}
